import React, { FC, useEffect, useState } from 'react'
import { RotateCcw, ChevronDown } from 'react-feather'
import { Dropdown, FilterTagParam } from '../../atoms'
import DropdownCheckboxList from '../Searchbar/DropdownCheckboxList/DropdownCheckboxList'
import DropdownRangeSelector from '../Searchbar/DropdownRangeSelector/DropdownRangeSelector'

import styles from './_.module.css'
import { Button } from 'antd'

export interface DropDownList {
  title: string
  type: string
  props: any
}

interface Props {
  dropdownList: DropDownList[]
  filters: any
  showSearch?: boolean
  onFilterChange: (n: string, v: any) => void
  onResetFilter: () => void
}

const ignoreFilterKeys = [
  'user_id',
  'sort_by',
  'sort_order',
  'current_page',
  'search_title',
  'company_id',
  'client_id'
]

// Component
export const LiveProjectsFilter: FC<Props> = ({
  dropdownList = [],
  filters = {},
  onFilterChange,
  onResetFilter
}) => {
  const [dropdownVisibility, setDropdownVisibility] = useState<
    Record<string, boolean>
  >({})

  const toggleDropdownVisibility = (name: string, status: boolean): void => {
    setDropdownVisibility(prevState => ({
      ...prevState,
      [name]: status
    }))
  }

  // Create Filter Tags on filter changeS
  useEffect(() => {
    if (filters) {
      /* generate tags */
      const _tags: FilterTagParam[] = []

      for (const key in filters) {
        const name = key
        const value = filters[key]

        // remove name in the ignored list
        if (ignoreFilterKeys.includes(key)) {
          continue
        }

        // if the value is an array then find the label from respective list
        if (Array.isArray(value)) {
          const _dropDownOption = dropdownList.find(d => d.props.name === name)
          const _list = _dropDownOption ? [..._dropDownOption?.props.list] : []

          value.forEach(selectedValue => {
            const _selectedOption = _list.find(
              o => o.id.toString() === selectedValue.toString()
            )

            // only push if the _selectionOption is contained in the list
            if (_selectedOption)
              _tags.push({
                key: name,
                label: _selectedOption.title || _selectedOption.name || '',
                value: selectedValue
              })
          })
        }

        // if value is string assign it directly
        if (typeof value === 'string') {
          _tags.push({
            key: name,
            label: value,
            value
          })
        }
      }
    }
  }, [filters])

  // Generate filter tags on filter change
  const handleFilterChange = (name: string, value: any) => {
    // create or remove tags
    onFilterChange(name, value)
  }

  // Reset all tags and filters
  const handleReset = () => {
    onResetFilter()
  }

  const list = dropdownList.map(d => {
    switch (d.type) {
      case 'DATE_RANGE':
        return {
          title: d.title,
          type: d.type,
          name: d.props.name,
          overlay: (
            <DropdownRangeSelector
              {...d.props}
              value={filters[d.props.name]}
              onFilterChange={(name, value) => handleFilterChange(name, value)}
              closeDropdown={(): void =>
                toggleDropdownVisibility(d.title, false)
              }
            />
          )
        }
      default:
        return {
          title: d.title,
          type: d.type,
          name: d.props.name,
          overlay: (
            <DropdownCheckboxList
              {...d.props}
              filters={filters}
              onFilterChange={(name, value) => handleFilterChange(name, value)}
              closeDropdown={(): void =>
                toggleDropdownVisibility(d.title, false)
              }
            />
          )
        }
    }
  })

  return (
    <>
      <div className={styles.dropdownContainer}>
        {list.map((item: any) => {
          if (item.type === 'BUTTON') {
            const isReworkFilterApplied = !!filters.client_rework_count
            return (
              <button
                key={item.id}
                className={`${styles.trigger} ${
                  isReworkFilterApplied ? styles.isActive : ''
                }`}
                onClick={() =>
                  handleFilterChange(
                    item.name,
                    isReworkFilterApplied ? undefined : ['0+']
                  )
                }
              >
                <RotateCcw size={16} />
                Reworked
              </button>
            )
          } else {
            const _getText = () => {
              const filterValue = filters?.[item?.name]
              if (
                filterValue?.length === 1 &&
                item.overlay.props.list.length > 0
              ) {
                return item.overlay.props.list.find(
                  (item: { id: number; title: string }) =>
                    item.id === filterValue[0]
                )?.title
              } else if (filterValue?.length > 1) {
                return `${filterValue?.length} ${item?.title}s`
              } else {
                return item?.title
              }
            }

            return (
              <Dropdown
                key={item.title}
                trigger={['click']}
                placement="bottomLeft"
                overlay={item.overlay}
                overlayClassName={styles.dropdownOverlay}
                visible={dropdownVisibility[item.title]}
                onVisibleChange={(status): void =>
                  toggleDropdownVisibility(item.title, status)
                }
              >
                <span
                  className={`${styles.trigger} ${
                    filters[item.name] ? styles.isActive : ''
                  }`}
                >
                  {_getText()}
                  <ChevronDown size={16} />
                </span>
              </Dropdown>
            )
          }
        })}
        {list.length > 0 && Object.keys(filters).length > 0 && (
          <Button
            type="text"
            className={styles.clearButton}
            onClick={(): void => handleReset()}
          >
            Clear Filters
          </Button>
        )}
      </div>
    </>
  )
}
